import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import AttendanceScanDialog from "../components/AttendanceScanDialog";
import * as attendanceApi from "../api/attendance";
import { formatDisplayDate } from "../utils/dateFormat";
import {
  playAttendanceScanBeep,
  playAttendanceScanErrorBeep,
  playAttendanceScanWarningBeep,
  unlockAttendanceScanAudio,
} from "../utils/attendanceScanBeep";
import "./AdminAttendance.css";

const SCAN_FEEDBACK_MS = 3000;

function draftKey(courseId, sessionDate) {
  return `attendance-draft:${courseId}:${sessionDate}`;
}

function loadDraft(courseId, sessionDate) {
  try {
    const raw = sessionStorage.getItem(draftKey(courseId, sessionDate));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.presents) ? parsed.presents : null;
  } catch {
    return null;
  }
}

function saveDraft(courseId, sessionDate, presents) {
  try {
    sessionStorage.setItem(
      draftKey(courseId, sessionDate),
      JSON.stringify({ presents: [...presents] }),
    );
  } catch {
    /* ignore quota */
  }
}

function clearDraft(courseId, sessionDate) {
  try {
    sessionStorage.removeItem(draftKey(courseId, sessionDate));
  } catch {
    /* ignore */
  }
}

function pickPreferredCameraId(devices) {
  if (!devices.length) return null;
  const back = devices.find((d) =>
    /back|rear|environment|world/i.test(d.label || ""),
  );
  return (back || devices[0]).deviceId;
}

const SCANNER_CONFIG = { fps: 8, qrbox: { width: 220, height: 220 } };
const CAMERA_RELEASE_MS = 200;
const CAMERA_RETRY_MS = 300;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isCameraBusyError(err) {
  const name = err?.name || "";
  const msg = String(err?.message || "");
  return (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    /not readable|in use|trackstart/i.test(msg)
  );
}

export default function AdminAttendanceTake() {
  const { courseId, date: sessionDate } = useParams();
  const navigate = useNavigate();
  const notificationCtx = React.useContext(NotificationContext);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { hasPermission, loading: permLoading } =
    useViewPermission("edit_attendance");
  const { busy, run } = useMutationLock();

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [roster, setRoster] = useState([]);
  const [session, setSession] = useState(null);
  const [alreadyCommitted, setAlreadyCommitted] = useState(false);
  const [presentIds, setPresentIds] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [scanDialog, setScanDialog] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  // Off by default — auto-start races with permission prompts and crashes html5-qrcode.
  const [scannerOn, setScannerOn] = useState(false);
  const [cameraCount, setCameraCount] = useState(0);
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  // True only while the permission probe getUserMedia is outstanding.
  const [awaitingCameraPermission, setAwaitingCameraPermission] =
    useState(false);

  const tokenMap = useMemo(() => {
    const map = new Map();
    for (const row of roster) {
      if (row.attendanceToken) {
        map.set(String(row.attendanceToken).trim().toLowerCase(), row);
      }
    }
    return map;
  }, [roster]);

  const scannerRef = useRef(null);
  const scanningLock = useRef(false);
  const scanLockTimerRef = useRef(null);
  const camerasRef = useRef([]);
  const activeCameraIdRef = useRef(null);
  const handleScanRef = useRef(null);
  // Bumped on scanner teardown so an in-flight switch cannot restart a dead instance.
  const cameraOpGenRef = useRef(0);
  const rosterRef = useRef(roster);
  const tokenMapRef = useRef(tokenMap);
  const presentIdsRef = useRef(presentIds);
  rosterRef.current = roster;
  tokenMapRef.current = tokenMap;
  presentIdsRef.current = presentIds;

  const showScanDialog = useCallback((message, status = "success") => {
    setScanDialog({ message, status });
  }, []);

  const closeScanDialog = useCallback(() => {
    if (scanLockTimerRef.current) {
      clearTimeout(scanLockTimerRef.current);
      scanLockTimerRef.current = null;
    }
    scanningLock.current = false;
    setScanDialog(null);
  }, []);

  const markPresent = useCallback(
    (registrationCourseId, source = "manual") => {
      const student = rosterRef.current.find(
        (r) => r.registrationCourseId === registrationCourseId,
      );
      const name = student?.name || "Student";

      if (presentIdsRef.current.has(registrationCourseId)) {
        if (source === "scan") {
          playAttendanceScanWarningBeep();
          showScanDialog(`${name} was already marked present.`, "info");
        }
        return;
      }

      setPresentIds((prev) => {
        const next = new Set(prev);
        next.add(registrationCourseId);
        saveDraft(courseId, sessionDate, next);
        return next;
      });

      if (source === "scan") {
        playAttendanceScanBeep();
        showScanDialog(name, "success");
      }
    },
    [courseId, sessionDate, showScanDialog],
  );

  const setManualStatus = (registrationCourseId, status) => {
    if (scannerOn) return;
    setPresentIds((prev) => {
      const next = new Set(prev);
      if (status === "present") next.add(registrationCourseId);
      else next.delete(registrationCourseId);
      saveDraft(courseId, sessionDate, next);
      return next;
    });
  };

  const handleScan = useCallback(
    (decodedText) => {
      if (scanningLock.current) return;
      scanningLock.current = true;
      const token = String(decodedText || "").trim().toLowerCase();
      const match = tokenMapRef.current.get(token);
      if (!match) {
        playAttendanceScanErrorBeep();
        showScanDialog(
          "QR not recognized. This code is not on today’s course roster.",
          "error",
        );
      } else {
        markPresent(match.registrationCourseId, "scan");
      }
      // Keep scanner locked while the confirmation dialog is visible;
      // dismiss clears this early via closeScanDialog.
      if (scanLockTimerRef.current) clearTimeout(scanLockTimerRef.current);
      scanLockTimerRef.current = setTimeout(() => {
        scanningLock.current = false;
        scanLockTimerRef.current = null;
      }, SCAN_FEEDBACK_MS + 200);
    },
    [markPresent, showScanDialog],
  );
  handleScanRef.current = handleScan;

  const switchCamera = useCallback(async () => {
    const list = camerasRef.current;
    const scanner = scannerRef.current;
    if (list.length < 2 || !scanner || cameraSwitching || !cameraReady) return;

    const previousId = activeCameraIdRef.current;
    const idx = Math.max(
      0,
      list.findIndex((d) => d.deviceId === previousId),
    );
    const next = list[(idx + 1) % list.length];
    if (!next?.deviceId || next.deviceId === previousId) return;

    const gen = cameraOpGenRef.current;
    const stillCurrent = () =>
      gen === cameraOpGenRef.current && scannerRef.current === scanner;

    const stopOrphaned = async () => {
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch {
        /* ignore */
      }
      try {
        await scanner.clear();
      } catch {
        /* ignore */
      }
    };

    const abortIfStale = async () => {
      if (stillCurrent()) return false;
      await stopOrphaned();
      return true;
    };

    const noteRunningCamera = () => {
      try {
        const runningId = scanner.getRunningTrackSettings?.()?.deviceId;
        if (runningId) activeCameraIdRef.current = runningId;
      } catch {
        /* ignore */
      }
    };

    const startDevice = async (deviceId) => {
      await scanner.start(
        { deviceId: { exact: deviceId } },
        SCANNER_CONFIG,
        (text) => handleScanRef.current?.(text),
        () => {},
      );
    };

    const startDeviceWithBusyRetry = async (deviceId) => {
      try {
        await startDevice(deviceId);
      } catch (err) {
        if (!stillCurrent() || !isCameraBusyError(err)) throw err;
        await sleep(CAMERA_RETRY_MS);
        if (!stillCurrent()) throw err;
        await startDevice(deviceId);
      }
    };

    setCameraSwitching(true);
    setCameraReady(false);
    setCameraError(null);
    try {
      if (scanner.isScanning) await scanner.stop();
      if (await abortIfStale()) return;

      await sleep(CAMERA_RELEASE_MS);
      if (await abortIfStale()) return;

      try {
        await startDeviceWithBusyRetry(next.deviceId);
      } catch (primaryErr) {
        if (await abortIfStale()) return;
        throw primaryErr;
      }

      if (await abortIfStale()) return;

      activeCameraIdRef.current = next.deviceId;
      noteRunningCamera();
      setCameraReady(true);
    } catch (err) {
      console.error("Switch camera failed:", err);
      if (await abortIfStale()) return;

      // Restore previous camera so the session is not left dead.
      if (previousId && previousId !== next.deviceId) {
        try {
          await sleep(CAMERA_RELEASE_MS);
          if (await abortIfStale()) return;
          await startDeviceWithBusyRetry(previousId);
          if (await abortIfStale()) return;
          activeCameraIdRef.current = previousId;
          noteRunningCamera();
          setCameraReady(true);
          return;
        } catch (restoreErr) {
          console.error("Restore camera failed:", restoreErr);
          if (await abortIfStale()) return;
        }
      }

      setCameraError(
        "Could not switch camera. Try Stop camera, then Start again.",
      );
      setScannerOn(false);
    } finally {
      if (gen === cameraOpGenRef.current) setCameraSwitching(false);
    }
  }, [cameraSwitching, cameraReady]);

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    const open = async () => {
      try {
        const res = await attendanceApi.openSession({
          courseId: Number(courseId),
          sessionDate,
        });
        setCourse(res.data.course);
        setRoster(res.data.roster || []);
        setSession(res.data.session);
        const committed = Boolean(res.data.alreadyCommitted);
        setAlreadyCommitted(committed);

        // Committed sessions always prefer server marks so a stale draft
        // cannot overwrite a previously saved record on re-open.
        if (committed) {
          clearDraft(courseId, sessionDate);
          const presents = (res.data.existingMarks || [])
            .filter((m) => m.status === "present")
            .map((m) => m.registrationCourseId);
          setPresentIds(new Set(presents));
        } else {
          const draft = loadDraft(courseId, sessionDate);
          if (draft && draft.length > 0) {
            setPresentIds(new Set(draft));
          } else if (res.data.existingMarks?.length) {
            const presents = res.data.existingMarks
              .filter((m) => m.status === "present")
              .map((m) => m.registrationCourseId);
            setPresentIds(new Set(presents));
          }
        }
      } catch (err) {
        console.error("Error opening session:", err);
        setCourse(null);
      } finally {
        setLoading(false);
      }
    };

    open();
  }, [hasPermission, courseId, sessionDate]);

  useEffect(() => {
    if (!scannerOn || !hasPermission || loading || !course) return undefined;

    let cancelled = false;
    const bootGen = cameraOpGenRef.current;
    const elementId = "attendance-qr-reader";

    const isStale = () =>
      cancelled || bootGen !== cameraOpGenRef.current;

    const failCamera = (message) => {
      if (isStale()) return;
      console.error("Camera error:", message);
      setCameraReady(false);
      setAwaitingCameraPermission(false);
      setCameraError(
        typeof message === "string"
          ? message
          : "Camera unavailable. Use manual toggles.",
      );
      setScannerOn(false);
    };

    const stopScannerSafe = async (scanner) => {
      if (!scanner) return;
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch {
        /* ignore */
      }
      try {
        await scanner.clear();
      } catch {
        /* ignore */
      }
    };

    const abandonScanner = async (scanner) => {
      await stopScannerSafe(scanner);
      if (scannerRef.current === scanner) scannerRef.current = null;
    };

    const permissionMessage = (err) => {
      const name = err?.name || "";
      const msg = String(err?.message || "");
      if (
        name === "NotAllowedError" ||
        name === "PermissionDeniedError" ||
        /permission|denied|notallowed/i.test(msg)
      ) {
        return "Camera permission denied. Allow camera in the browser address bar, then click Start camera again.";
      }
      if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return "No camera found. Use manual toggles.";
      }
      if (name === "NotReadableError" || name === "TrackStartError") {
        return "Camera is in use by another app. Close it and try again.";
      }
      return "Camera unavailable. Use manual toggles.";
    };

    const startScanner = async () => {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          failCamera(
            "Camera not supported in this browser (needs HTTPS or localhost). Use manual toggles.",
          );
          return;
        }

        const el = document.getElementById(elementId);
        if (!el) {
          failCamera("Scanner area not ready. Try Start camera again.");
          return;
        }

        setCameraError(null);
        setCameraReady(false);
        setAwaitingCameraPermission(true);
        setCameraCount(0);
        camerasRef.current = [];
        activeCameraIdRef.current = null;

        // Explicit permission prompt — do not rely on getCameras() (often returns
        // empty before permission and turns the scanner off with no prompt).
        let permissionStream = null;
        try {
          permissionStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
          });
        } catch (err) {
          // Retry with any camera if environment facingMode is unsupported.
          try {
            permissionStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });
          } catch (err2) {
            setAwaitingCameraPermission(false);
            failCamera(permissionMessage(err2));
            return;
          }
        }

        if (isStale()) {
          permissionStream?.getTracks?.().forEach((track) => track.stop());
          setAwaitingCameraPermission(false);
          return;
        }

        setAwaitingCameraPermission(false);

        // Release the probe stream so html5-qrcode can open the device.
        permissionStream?.getTracks?.().forEach((track) => track.stop());
        permissionStream = null;

        // Some mobile browsers need a beat before the device is free again.
        await sleep(CAMERA_RELEASE_MS);
        if (isStale()) return;

        let videoInputs = [];
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          videoInputs = devices.filter(
            (d) => d.kind === "videoinput" && d.deviceId,
          );
        } catch {
          videoInputs = [];
        }

        if (isStale()) return;

        camerasRef.current = videoInputs;
        setCameraCount(videoInputs.length);
        const preferredId = pickPreferredCameraId(videoInputs);
        activeCameraIdRef.current = preferredId;

        if (scannerRef.current) {
          const prev = scannerRef.current;
          scannerRef.current = null;
          await stopScannerSafe(prev);
        }

        if (isStale()) return;

        const scanner = new Html5Qrcode(elementId);
        if (isStale()) {
          await abandonScanner(scanner);
          return;
        }
        scannerRef.current = scanner;

        const onSuccess = (text) => handleScanRef.current?.(text);
        const onFailure = () => {};

        const noteRunningCamera = () => {
          try {
            const runningId = scanner.getRunningTrackSettings?.()?.deviceId;
            if (runningId) activeCameraIdRef.current = runningId;
          } catch {
            /* ignore */
          }
        };

        const startWithBusyRetry = async (constraint) => {
          try {
            await scanner.start(
              constraint,
              SCANNER_CONFIG,
              onSuccess,
              onFailure,
            );
          } catch (err) {
            if (!isCameraBusyError(err) || isStale()) throw err;
            await sleep(CAMERA_RETRY_MS);
            if (isStale()) throw err;
            await scanner.start(
              constraint,
              SCANNER_CONFIG,
              onSuccess,
              onFailure,
            );
          }
        };

        const finishStartOrAbandon = async () => {
          if (isStale()) {
            await abandonScanner(scanner);
            return false;
          }
          noteRunningCamera();
          setCameraReady(true);
          return true;
        };

        if (preferredId) {
          try {
            await startWithBusyRetry({ deviceId: { exact: preferredId } });
            await finishStartOrAbandon();
            return;
          } catch {
            if (isStale()) {
              await abandonScanner(scanner);
              return;
            }
            // Fall through to facingMode if the chosen device fails.
          }
        }

        try {
          await startWithBusyRetry({ facingMode: "environment" });
          await finishStartOrAbandon();
        } catch {
          if (isStale()) {
            await abandonScanner(scanner);
            return;
          }
          await startWithBusyRetry({ facingMode: "user" });
          await finishStartOrAbandon();
        }
      } catch (err) {
        failCamera(permissionMessage(err));
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      cameraOpGenRef.current += 1;
      const current = scannerRef.current;
      scannerRef.current = null;
      camerasRef.current = [];
      activeCameraIdRef.current = null;
      setCameraCount(0);
      setCameraSwitching(false);
      setCameraReady(false);
      setAwaitingCameraPermission(false);
      stopScannerSafe(current);
    };
  }, [scannerOn, hasPermission, loading, course]);

  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        String(r.registrationId).includes(q),
    );
  }, [roster, search]);

  const handleCommit = async () => {
    await run(async () => {
      try {
        const res = await attendanceApi.commitSession({
          courseId: Number(courseId),
          sessionDate,
          sessionId: session?.id,
          presents: [...presentIds],
        });
        clearDraft(courseId, sessionDate);
        notificationCtx.show({
          message: `Attendance saved — Present ${res.data.counts.present}, Absent ${res.data.counts.absent}, Total ${res.data.counts.total}`,
          stat: "success",
        });
        navigate(
          `/admin/attendance/${courseId}/sessions/${res.data.session.id}`,
        );
      } catch (err) {
        console.error("Commit failed:", err);
        notificationCtx.show({
          message: err?.response?.data?.message || "Failed to save attendance",
          stat: "error",
        });
      }
    });
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <Navbar />
        <div className="att-shell">
          <div className="att-empty">
            <div className="att-empty__title">Unable to open this session</div>
            <div className="att-empty__hint" style={{ marginBottom: 14 }}>
              Check your access or try again from the course page.
            </div>
            <Link to="/admin/attendance" className="att-btn att-btn--ghost">
              Back to attendance
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="att-shell">
        <div className="att-banner">
          <div className="att-banner__row">
            <div>
              <p className="att-banner__crumb">
                <Link to="/admin/attendance">Attendance courses</Link>
                {" / "}
                <Link to={`/admin/attendance/${courseId}`}>{course.title}</Link>
                {" / "}
                Mark class
              </p>
              <h1 className="att-banner__title">MARK CLASS ATTENDANCE</h1>
              <p className="att-banner__subtitle">
                {course.title} · {formatDisplayDate(sessionDate)}
              </p>
              {alreadyCommitted ? (
                <p className="att-banner-note">
                  This date already has a saved session. Uploading will replace
                  all marks for the current roster.
                </p>
              ) : null}
            </div>

            <div className="att-banner__controls">
              <div className="att-banner__control-group">
                <div className="att-banner__control-label">Live counts</div>
                <div className="att-stats att-stats--banner">
                  <div className="att-stat">
                    Present
                    <strong>{presentIds.size}</strong>
                  </div>
                  <div className="att-stat">
                    Remaining
                    <strong>
                      {Math.max(roster.length - presentIds.size, 0)}
                    </strong>
                  </div>
                  <div className="att-stat">
                    Roster
                    <strong>{roster.length}</strong>
                  </div>
                </div>
              </div>

              <div className="att-banner__control-group">
                <div className="att-banner__control-label">Save session</div>
                <div className="att-banner__actions">
                  <button
                    type="button"
                    className="att-btn att-btn--mint"
                    disabled={busy}
                    onClick={handleCommit}
                  >
                    {busy
                      ? "Uploading…"
                      : alreadyCommitted
                        ? "Re-upload attendance"
                        : "Upload attendance"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="att-spacer" />

        <div className="att-take-grid">
          <section className="att-panel">
            <div className="att-panel__head">
              <h2 className="att-panel__title">SCAN STUDENT QR</h2>
              <div className="att-panel__actions">
                {scannerOn && cameraCount > 1 ? (
                  <button
                    type="button"
                    className="att-btn att-btn--ghost"
                    style={{ height: 34 }}
                    onClick={switchCamera}
                    disabled={cameraSwitching || !cameraReady}
                  >
                    {cameraSwitching ? "Switching…" : "Switch camera"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="att-btn att-btn--lavender"
                  style={{ height: 34 }}
                  onClick={() => {
                    if (cameraSwitching) {
                      // Abort in-flight switch before tearing down.
                      cameraOpGenRef.current += 1;
                      setCameraSwitching(false);
                    }
                    setCameraError(null);
                    setScannerOn((v) => {
                      const next = !v;
                      if (next) {
                        // Unlock audio on this click so success beeps can play later.
                        unlockAttendanceScanAudio();
                      }
                      return next;
                    });
                  }}
                >
                  {scannerOn ? "Stop camera" : "Start camera"}
                </button>
              </div>
            </div>
            <div className="att-panel__body">
              <div
                id="attendance-qr-reader"
                className="att-scanner"
                style={{ display: scannerOn ? "block" : "none" }}
              />
              {!scannerOn ? (
                <div className="att-empty" style={{ padding: "28px 8px" }}>
                  <div className="att-empty__title" style={{ fontSize: 15 }}>
                    Camera is off
                  </div>
                  <div className="att-empty__hint">
                    {cameraError ||
                      "Start the camera to scan, or mark students manually."}
                  </div>
                </div>
              ) : cameraError ? (
                <p className="att-feedback att-feedback--err">{cameraError}</p>
              ) : !cameraReady ? (
                <p className="att-feedback">
                  {cameraSwitching
                    ? "Switching camera…"
                    : awaitingCameraPermission
                      ? "Waiting for camera permission… Allow access when the browser asks."
                      : "Starting camera…"}
                </p>
              ) : (
                <p className="att-feedback">
                  Point the camera at a student QR code.
                </p>
              )}
            </div>
          </section>

          <section className="att-panel">
            <div className="att-panel__head">
              <h2 className="att-panel__title">MARK BY NAME</h2>
              {scannerOn ? (
                <span className="att-panel__lock">Locked while scanning</span>
              ) : null}
            </div>
            <div className="att-panel__body">
              <input
                className="att-field att-search"
                type="search"
                placeholder="Search name, email, or reg id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <ul
                className={`att-roster${
                  scannerOn ? " att-roster--locked" : ""
                }`}
              >
                {filteredRoster.map((row) => {
                  const present = presentIds.has(row.registrationCourseId);
                  return (
                    <li
                      key={row.registrationCourseId}
                      className="att-roster__item"
                    >
                      <div>
                        <div className="att-roster__name">{row.name}</div>
                        <div className="att-roster__meta">
                          Reg #{row.registrationId}
                          {row.email ? ` · ${row.email}` : ""}
                        </div>
                      </div>
                      <label
                        className={`att-switch${
                          present ? " is-on" : " is-off"
                        }${scannerOn ? " is-disabled" : ""}`}
                      >
                        <span className="att-switch__label" aria-hidden="true">
                          {present ? "Present" : "Absent"}
                        </span>
                        <input
                          type="checkbox"
                          role="switch"
                          checked={present}
                          disabled={scannerOn}
                          aria-label={`Mark ${row.name} ${
                            present ? "absent" : "present"
                          }`}
                          onChange={(e) =>
                            setManualStatus(
                              row.registrationCourseId,
                              e.target.checked ? "present" : "absent",
                            )
                          }
                        />
                        <span className="att-switch__track" aria-hidden="true">
                          <span className="att-switch__thumb" />
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        </div>
      </div>

      <AttendanceScanDialog
        open={Boolean(scanDialog)}
        message={scanDialog?.message}
        status={scanDialog?.status || "success"}
        durationMs={SCAN_FEEDBACK_MS}
        onClose={closeScanDialog}
      />
    </div>
  );
}
